import { useEffect, useState } from 'react';
import { Link, useNavigate, useOutletContext, useParams } from 'react-router-dom';
import { api, fmtDate } from '../lib/api.js';
import { BatchPLSummary, BatchTransactions, BatchDistributionPreview } from '../components/BatchDrillIn.jsx';

const EVENT_OPTIONS = [
  { value: 'mortality',      label: 'Mortality',     color: 'bg-rust-500/15 text-rust-700' },
  { value: 'addition',       label: 'Addition',      color: 'bg-olive-100 text-olive-900' },
  { value: 'processing',     label: 'Processing',    color: 'bg-gold-500/15 text-gold-600' },
  { value: 'eggs_collected', label: 'Eggs collected', color: 'bg-gold-500/15 text-gold-600' },
  { value: 'culled',         label: 'Culled',        color: 'bg-ink-700/10 text-ink-700' },
  { value: 'sold',           label: 'Sold',          color: 'bg-olive-100 text-olive-900' },
  { value: 'retired',        label: 'Retired',       color: 'bg-ink-700/10 text-ink-700' },
];

export default function ChickensDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isOwner } = useOutletContext();
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');
  const [form, setForm] = useState({ event_type: 'mortality', date: new Date().toISOString().slice(0, 10), count: '', notes: '' });
  const [busy, setBusy] = useState(false);

  async function toggleHidden() {
    const next = data.batch.status === 'active' ? 'retired' : 'active';
    try { await api.patch(`/api/chickens/${id}`, { status: next }); load(); }
    catch (e) { setErr(e.message); }
  }
  async function deleteBatch() {
    const b = data.batch;
    const label = `${b.type === 'broiler' ? 'Broiler batch' : 'Layer flock'} "${b.name}"`;
    if (!confirm(
      `Delete ${label}?\n\n` +
      `• All ${b.type === 'broiler' ? 'mortality / processing' : 'mortality / egg-collection'} events for this batch will be deleted.\n` +
      `• Income and expense rows tagged to this batch will stay, but un-tagged (they roll up to the segment level — meat_chickens or egg_chickens).\n` +
      `• Hidden batches still count for historical Batch P&L. If you only want to declutter, click Hide instead.\n\n` +
      `Type "delete" in the next prompt to confirm.`,
    )) return;
    const typed = prompt(`Confirm: type "delete" exactly to remove ${label}`);
    if (typed !== 'delete') return;
    try {
      const r = await api.delete(`/api/chickens/${id}`);
      const u = r.untagged || {};
      alert(`✓ Deleted. Un-tagged ${u.income || 0} income row(s), ${u.expense_lines || 0} expense line(s), and removed ${u.events || 0} event(s).`);
      navigate('/chickens');
    } catch (e) { setErr(e.message); }
  }

  async function load() {
    try { setData(await api.get(`/api/chickens/${id}`)); } catch (e) { setErr(e.message); }
  }
  useEffect(() => { load(); }, [id]);

  if (err) return <div className="text-rust-700 text-sm">{err}</div>;
  if (!data) return <div className="text-ink-700/60 italic">Loading…</div>;
  const b = data.batch;

  async function addEvent(e) {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post(`/api/chickens/${id}/events`, {
        event_type: form.event_type,
        date: form.date,
        count: Number(form.count),
        notes: form.notes.trim() || null,
      });
      setForm(f => ({ ...f, count: '', notes: '' }));
      load();
    } catch (e) { setErr(e.message); }
    finally { setBusy(false); }
  }

  // Quick mortality buttons (the common case)
  async function quickMortality(n) {
    if (!confirm(`Log ${n} mortality today?`)) return;
    await api.post(`/api/chickens/${id}/events`, {
      event_type: 'mortality',
      date: new Date().toISOString().slice(0, 10),
      count: n,
      notes: null,
    });
    load();
  }

  async function logEggs(count) {
    await api.post(`/api/chickens/${id}/events`, {
      event_type: 'eggs_collected',
      date: new Date().toISOString().slice(0, 10),
      count,
      notes: null,
    });
    load();
  }

  const startCount = (b.count_in || 0) + data.totals.additions;

  return (
    <div className="space-y-6">
      <Link to="/chickens" className="text-sm text-olive-700 hover:underline">‹ Chickens</Link>

      <header className="card">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="flex-1 min-w-0">
            <h1 className="text-2xl font-serif text-olive-900">
              {b.type === 'broiler' ? '🍗' : '🥚'} {b.name}
              {b.status !== 'active' && <span className="ml-3 pill bg-ink-700/10 text-ink-700">{b.status}</span>}
            </h1>
            <div className="text-sm text-ink-700/70 mt-1 capitalize">
              {b.type === 'broiler' ? 'Broiler batch' : 'Layer flock'}
              {b.source && <> · {b.source}</>}
              {b.source_date && <> · in {fmtDate(b.source_date)}</>}
              {b.expected_processing_date && <> · proc {fmtDate(b.expected_processing_date)}</>}
            </div>
          </div>
          {isOwner && (
            <div className="flex gap-2 text-xs">
              <button onClick={toggleHidden} className="btn-ghost text-xs py-1 px-2">
                {b.status === 'active' ? 'Hide' : 'Unhide'}
              </button>
              <button onClick={deleteBatch} className="text-xs text-rust-700 hover:underline px-2">
                Delete
              </button>
            </div>
          )}
        </div>
        <dl className="grid grid-cols-3 sm:grid-cols-5 gap-x-4 gap-y-2 mt-4 text-sm">
          <Field label="Start"><strong>{startCount}</strong></Field>
          <Field label="Mortality" tone="rust">{data.totals.mortality}</Field>
          <Field label="Processed">{data.totals.processed}</Field>
          <Field label="Removed">{data.totals.removed}</Field>
          <Field label="On hand" tone="olive"><strong className="text-lg">{data.on_hand}</strong></Field>
        </dl>
        {b.notes && <div className="mt-3 text-sm text-ink-700/80 italic">{b.notes}</div>}
      </header>

      {isOwner && (
        <div className="flex flex-wrap gap-2">
          <button
            className="btn-primary text-sm"
            onClick={() => navigate(`/income/new?segment=${b.type === 'broiler' ? 'meat_chickens' : 'egg_chickens'}&chicken_batch_id=${b.id}`)}>
            + Log income for this batch
          </button>
          <button
            className="btn-ghost text-sm"
            onClick={() => navigate(`/receipts/new?segment=${b.type === 'broiler' ? 'meat_chickens' : 'egg_chickens'}&chicken_batch_id=${b.id}`)}>
            + Log receipt for this batch
          </button>
        </div>
      )}

      <BatchPLSummary
        scope={{ chicken_batch_id: b.id }}
        segmentId={b.type === 'broiler' ? 'meat_chickens' : 'egg_chickens'}
      />

      <BatchTransactions
        scope={{ chicken_batch_id: b.id }}
        isOwner={isOwner}
      />

      {isOwner && (
        <BatchDistributionPreview
          scope={{ chicken_batch_id: b.id }}
          segmentId={b.type === 'broiler' ? 'meat_chickens' : 'egg_chickens'}
        />
      )}

      {b.type === 'layer' && (
        <section className="card space-y-3">
          <h2 className="font-medium text-olive-900">🥚 Today's eggs</h2>
          <EggEntry onLog={logEggs} todayCount={data.events.find(e => e.event_type === 'eggs_collected' && e.date === new Date().toISOString().slice(0, 10))?.count} />
        </section>
      )}

      <section className="card space-y-3">
        <h2 className="font-medium text-olive-900">Quick mortality</h2>
        <div className="flex flex-wrap gap-2">
          {[1, 2, 3, 5, 10].map(n => (
            <button key={n} onClick={() => quickMortality(n)} className="btn-rust">−{n}</button>
          ))}
        </div>
        <p className="text-xs text-ink-700/60">Common case: birds you found dead today. Logs to today's date with no notes.</p>
      </section>

      <section className="card space-y-3">
        <h2 className="font-medium text-olive-900">Add event</h2>
        <form onSubmit={addEvent} className="grid grid-cols-12 gap-2 items-end">
          <div className="col-span-4">
            <label className="label">Type</label>
            <select className="input" value={form.event_type} onChange={e => setForm(f => ({ ...f, event_type: e.target.value }))}>
              {EVENT_OPTIONS.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
          </div>
          <div className="col-span-3">
            <label className="label">Date</label>
            <input className="input" type="date" value={form.date} onChange={e => setForm(f => ({ ...f, date: e.target.value }))} />
          </div>
          <div className="col-span-2">
            <label className="label">Count</label>
            <input className="input" type="number" min="1" required value={form.count} onChange={e => setForm(f => ({ ...f, count: e.target.value }))} />
          </div>
          <div className="col-span-3">
            <label className="label">Notes</label>
            <input className="input" value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} placeholder="cause, processor, buyer…" />
          </div>
          <div className="col-span-12 flex justify-end">
            <button className="btn-primary" disabled={busy || !form.count}>{busy ? 'Saving…' : 'Add event'}</button>
          </div>
        </form>
      </section>

      <section className="card">
        <h2 className="font-medium text-olive-900 mb-3">Event history</h2>
        {data.events.length === 0 ? (
          <p className="text-sm text-ink-700/60 italic">No events yet.</p>
        ) : (
          <ul className="space-y-2">
            {data.events.map(ev => {
              const opt = EVENT_OPTIONS.find(o => o.value === ev.event_type) || { color: 'bg-ink-700/10 text-ink-700', label: ev.event_type };
              return (
                <li key={ev.id} className="border-l-2 border-olive-100 pl-3 py-1">
                  <div className="text-sm flex items-center gap-2">
                    <span className={`pill ${opt.color}`}>{opt.label}</span>
                    <span className="font-semibold text-ink-900">{ev.count}</span>
                    {ev.cause_or_notes && <span className="text-ink-700/80">— {ev.cause_or_notes}</span>}
                  </div>
                  <div className="text-xs text-ink-700/60 mt-0.5">
                    {fmtDate(ev.date)}
                    {ev.user_name && <> · by {ev.user_name}</>}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </section>
    </div>
  );
}

function EggEntry({ onLog, todayCount }) {
  const [n, setN] = useState('');
  const [busy, setBusy] = useState(false);
  async function submit(e) {
    e.preventDefault();
    if (!n) return;
    setBusy(true);
    try { await onLog(Number(n)); setN(''); }
    finally { setBusy(false); }
  }
  return (
    <div className="space-y-2">
      {todayCount != null && (
        <div className="text-sm text-olive-900">
          Already logged <strong>{todayCount}</strong> today. Add more below to record additional collections.
        </div>
      )}
      <form onSubmit={submit} className="flex gap-2 items-end">
        <div className="flex-1">
          <label className="label">Eggs collected this round</label>
          <input
            className="input"
            type="number"
            min="1"
            value={n}
            onChange={e => setN(e.target.value)}
            placeholder="e.g. 18"
            inputMode="numeric"
          />
        </div>
        <button className="btn-primary" disabled={busy || !n}>{busy ? '…' : 'Log'}</button>
      </form>
      <div className="flex gap-2">
        {[6, 12, 18, 24].map(quick => (
          <button key={quick} type="button" onClick={() => onLog(quick)} disabled={busy} className="btn-ghost text-xs py-1 px-2">+{quick}</button>
        ))}
      </div>
    </div>
  );
}

function Field({ label, children, tone }) {
  const c = tone === 'rust' ? 'text-rust-700' : tone === 'olive' ? 'text-olive-900' : 'text-ink-900';
  return (
    <div>
      <dt className="text-xs uppercase tracking-wider text-ink-700/60">{label}</dt>
      <dd className={`mt-0.5 ${c}`}>{children}</dd>
    </div>
  );
}
